import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Link,
    Typography
} from '@mui/material'
import ReactMarkdown from 'react-markdown'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingIndicator from 'components/ui/loading-indicator'
import { useGetTermsOfServiceQuery } from 'services/laravel_api/termsOfService'

export default function AcceptanceTermsCheckbox({
    value,
    onChange,
    onAcceptedTerms
}: Readonly<{
    value?: boolean
    onChange?: (hasAcceptedTerms: boolean, version: string) => void
    onAcceptedTerms?: (version: string) => Promise<void>
}>) {
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(value ?? false)
    const [isCallbackLoading, setIsCallbackLoading] = useState(false)
    const [termsModalOpen, setTermsModalOpen] = useState(false)
    const { t } = useTranslation()

    const termsOfService = useGetTermsOfServiceQuery(undefined)

    const isDisabled = useMemo(() => {
        if (termsOfService.error) {
            return true
        }

        return isCallbackLoading || termsOfService.isLoading
    }, [isCallbackLoading, termsOfService.isLoading, termsOfService.error])

    async function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.checked

        const termsVersion = termsOfService.data?.version_tag

        if (!termsVersion) {
            throw new Error('Terms of Service version is not available')
        }

        if (onAcceptedTerms && value) {
            setIsCallbackLoading(true)

            await onAcceptedTerms(termsVersion).finally(() =>
                setIsCallbackLoading(false)
            )
        }

        setHasAcceptedTerms(value)

        if (onChange) {
            onChange(value, termsVersion)
        }
    }

    return (
        <>
            <FormControlLabel
                control={
                    <Checkbox
                        disabled={isDisabled}
                        sx={{
                            '&.Mui-disabled': {
                                color: (theme) =>
                                    theme.palette.custom.text.lightGrey
                            }
                        }}
                        checked={hasAcceptedTerms}
                        onChange={handleOnChange}
                        name="termsAccepted"
                        color="primary"
                    />
                }
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            I have read and agree to the{' '}
                            {t('terms_and_services_name')}{' '}
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                disabled={isCallbackLoading}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setTermsModalOpen(true)
                                }}
                                sx={{ verticalAlign: 'baseline' }}
                            >
                                Terms of Service
                            </Link>
                            .
                        </Typography>
                        <LoadingIndicator
                            isLoading={
                                isCallbackLoading || termsOfService.isLoading
                            }
                            size={16}
                        />
                    </Box>
                }
                sx={{ mt: 2 }}
            />

            {termsOfService.isLoading === false && termsOfService.error && (
                <Alert sx={{ mb: 1 }} severity="error">
                    Failed to load Terms of Service.
                </Alert>
            )}

            <Dialog
                open={termsModalOpen}
                onClose={() => setTermsModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Terms & Services</DialogTitle>
                <DialogContent dividers>
                    {termsOfService.isLoading ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                height: 100
                            }}
                        >
                            <LoadingIndicator isLoading size={32} />
                        </Box>
                    ) : (
                        <ReactMarkdown>
                            {termsOfService.data?.content}
                        </ReactMarkdown>
                    )}
                    {termsOfService.isLoading === false &&
                        termsOfService.error && (
                            <Typography>
                                Failed to load Terms of Service.
                            </Typography>
                        )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTermsModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
